import { ForcedReason } from '@/types/tournament.model';
import { useSlotOccupancyOpener } from './slot-occupancy-context';

interface ForcedReasonBadgeProps {
  reason?: ForcedReason | null;
  /** Enables the tap that opens the slot detail. Without it the badge is a plain label. */
  matchId?: string;
}

/**
 * Single source for "UNSCHEDULED is red, OVERBOOKED is amber". The per-match badge, the aggregate
 * warning next to the DRAFT chip and the slot detail sheet all read from here — the warning copy
 * tells the organizer to look for the badge by colour, so the three must not drift apart.
 *
 * `title` doubles as the sheet's description, so it says what to do about the mark and stops
 * there: the numbers of the slot go in the sheet, which is the only place that knows them.
 */
export const FORCED_REASON_STYLES: Record<
  ForcedReason,
  { label: string; title: string; badge: string; banner: string }
> = {
  [ForcedReason.OVERBOOKED]: {
    label: 'Cancha solapada',
    title:
      'Hay más partidos que canchas en esta franja. No bloquea la activación, pero conviene mover alguno a otra hora.',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    banner: 'bg-amber-50 text-amber-800 border-amber-300',
  },
  [ForcedReason.UNSCHEDULED]: {
    label: 'Sin horario',
    title:
      'El horario es provisional y no se puede jugar así. Asignale un horario antes de activar el torneo.',
    badge: 'bg-red-100 text-red-800 border-red-300',
    banner: 'bg-red-50 text-red-800 border-red-300',
  },
};

export function ForcedReasonBadge({ reason, matchId }: ForcedReasonBadgeProps) {
  const openSlotDetail = useSlotOccupancyOpener();
  if (!reason) return null;
  const { label, title, badge } = FORCED_REASON_STYLES[reason];
  const className = `inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${badge}`;

  if (!openSlotDetail || !matchId) {
    return (
      <span title={title} className={className}>
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={title}
      aria-haspopup="dialog"
      onClick={(event) => {
        // The badge sits inside cards that open the match editor on click.
        event.stopPropagation();
        openSlotDetail(matchId, reason);
      }}
      className={`${className} underline decoration-dotted underline-offset-2 transition-opacity hover:opacity-80`}
    >
      {label}
    </button>
  );
}
